import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient, useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Package, 
  Plus, 
  AlertTriangle,
  Clock,
  Search,
  Edit,
  Trash,
  ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const InventoryPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState('');
  const { isDoctor } = useAuth();

  const filter = searchParams.get('filter') || '';
  const category = searchParams.get('category') || '';

  const [form, setForm] = useState({
    name: '',
    category: 'medicine',
    quantity: '',
    unit: 'pieces',
    expiry_date: '',
    reorder_threshold: '10'
  });

  useEffect(() => {
    fetchInventory();
  }, [filter, category]);

  const fetchInventory = async () => {
    try {
      let url = '/inventory?';
      if (filter === 'lowstock') url += 'low_stock=true&';
      if (filter === 'expiring') url += 'expiring_soon=true&';
      if (category) url += `category=${category}`;
      
      const response = await apiClient.get(url);
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.quantity) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (editingItem) {
        await apiClient.put(`/inventory/${editingItem.item_id}`, {
          ...form,
          quantity: parseInt(form.quantity),
          reorder_threshold: parseInt(form.reorder_threshold)
        });
        toast.success('Item updated');
      } else {
        await apiClient.post('/inventory', {
          ...form,
          quantity: parseInt(form.quantity),
          reorder_threshold: parseInt(form.reorder_threshold)
        });
        toast.success('Item added');
      }
      setShowForm(false);
      setEditingItem(null);
      setForm({
        name: '',
        category: 'medicine',
        quantity: '',
        unit: 'pieces',
        expiry_date: '',
        reorder_threshold: '10'
      });
      fetchInventory();
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name,
      category: item.category,
      quantity: item.quantity.toString(),
      unit: item.unit,
      expiry_date: item.expiry_date || '',
      reorder_threshold: item.reorder_threshold.toString()
    });
    setShowForm(true);
  };

  const handleDelete = async () => {
    try {
      await apiClient.delete(`/inventory/${deleteId}`);
      toast.success('Item deleted');
      setDeleteId(null);
      fetchInventory();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleAdjust = async (itemId, change) => {
    try {
      await apiClient.post(`/inventory/${itemId}/adjust?quantity_change=${change}`);
      toast.success('Quantity updated');
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust quantity');
    }
  };

  const filteredItems = items.filter(item => 
    item.name.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: items.length,
    lowStock: items.filter(i => i.is_low_stock).length,
    expiring: items.filter(i => i.is_expiring_soon).length,
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-3">
            <Package className="w-6 h-6 text-primary" strokeWidth={1.5} />
            Inventory
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {items.length} items tracked
          </p>
        </div>
        <Button 
          onClick={() => {
            setEditingItem(null);
            setForm({
              name: '',
              category: 'medicine',
              quantity: '',
              unit: 'pieces',
              expiry_date: '',
              reorder_threshold: '10'
            });
            setShowForm(true);
          }}
          className="rounded-full"
          data-testid="add-item-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Item
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card 
          className={`cursor-pointer ${!filter ? 'border-primary' : ''}`}
          onClick={() => {
            searchParams.delete('filter');
            setSearchParams(searchParams);
          }}
        >
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total Items</p>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer border-amber-200 ${filter === 'expiring' ? 'ring-2 ring-amber-500' : 'bg-amber-50/50'}`}
          onClick={() => {
            searchParams.set('filter', 'expiring');
            setSearchParams(searchParams);
          }}
        >
          <CardContent className="p-4 text-center">
            <Clock className="w-5 h-5 text-amber-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-amber-600">{stats.expiring}</p>
            <p className="text-xs text-muted-foreground">Expiring in 6 mo</p>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer border-red-200 ${filter === 'lowstock' ? 'ring-2 ring-red-500' : 'bg-red-50/50'}`}
          onClick={() => {
            searchParams.set('filter', 'lowstock');
            setSearchParams(searchParams);
          }}
        >
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-red-600">{stats.lowStock}</p>
            <p className="text-xs text-muted-foreground">Low Stock</p>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-11"
            data-testid="inventory-search"
          />
        </div>
        <Select 
          value={category || 'all'} 
          onValueChange={(v) => {
            if (v === 'all') {
              searchParams.delete('category');
            } else {
              searchParams.set('category', v);
            }
            setSearchParams(searchParams);
          }}
        >
          <SelectTrigger className="w-40" data-testid="category-filter">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="medicine">Medicines</SelectItem>
            <SelectItem value="supplies">Supplies</SelectItem>
            <SelectItem value="equipment">Equipment</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Inventory List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No items found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filteredItems.map((item, index) => (
            <Card 
              key={item.item_id}
              className={`animate-fade-in ${item.is_low_stock || item.is_expiring_soon ? 'border-l-4' : ''} ${item.is_low_stock ? 'border-l-red-500' : ''} ${item.is_expiring_soon && !item.is_low_stock ? 'border-l-amber-500' : ''}`}
              style={{ animationDelay: `${index * 0.03}s` }}
              data-testid={`inventory-item-${item.item_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium">{item.name}</h3>
                      <Badge variant="outline" className="capitalize">{item.category}</Badge>
                      {item.is_low_stock && (
                        <Badge variant="destructive" className="text-xs">Low Stock</Badge>
                      )}
                      {item.is_expiring_soon && (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">Expiring Soon</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                      <span>
                        <strong className={item.is_low_stock ? 'text-red-600' : ''}>{item.quantity}</strong> {item.unit}
                      </span>
                      <span>Reorder at: {item.reorder_threshold}</span>
                      {item.expiry_date && (
                        <span className={item.is_expiring_soon ? 'text-amber-600' : ''}>
                          Expires: {item.expiry_date}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex items-center border rounded-lg">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleAdjust(item.item_id, -1)}
                      >
                        -
                      </Button>
                      <span className="w-10 text-center text-sm">{item.quantity}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleAdjust(item.item_id, 1)}
                      >
                        +
                      </Button>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    {isDoctor && (
                      <Button variant="ghost" size="icon" onClick={() => setDeleteId(item.item_id)}>
                        <Trash className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Item name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="medicine">Medicine</SelectItem>
                    <SelectItem value="supplies">Supplies</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pieces">Pieces</SelectItem>
                    <SelectItem value="tablets">Tablets</SelectItem>
                    <SelectItem value="capsules">Capsules</SelectItem>
                    <SelectItem value="bottles">Bottles</SelectItem>
                    <SelectItem value="boxes">Boxes</SelectItem>
                    <SelectItem value="rolls">Rolls</SelectItem>
                    <SelectItem value="pairs">Pairs</SelectItem>
                    <SelectItem value="units">Units</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div>
                <Label>Reorder Threshold</Label>
                <Input
                  type="number"
                  value={form.reorder_threshold}
                  onChange={(e) => setForm({ ...form, reorder_threshold: e.target.value })}
                  placeholder="10"
                />
              </div>
            </div>
            <div>
              <Label>Expiry Date (if applicable)</Label>
              <Input
                type="date"
                value={form.expiry_date}
                onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-item-btn">
              {editingItem ? 'Save Changes' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Item?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this inventory item. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InventoryPage;
